import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PAGE_SEO } from "@/data/seo";

const TRUCKS = [
  {
    id: "small",
    name: "Small Truck",
    size: "3 Tonne",
    price: 69,
    img: "/images/truck-small.png",
    bestFor: ["Studio Apartment", "1 Bedroom Home", "Student Move", "Few Furniture Items", "Small Office"],
    includes: ["1 Professional Mover", "Tail Lift", "Moving Blankets", "Trolley & Straps", "Furniture Protection", "Loading & Unloading", "Basic Disassembly & Reassembly"],
    featured: false,
  },
  {
    id: "medium",
    name: "Medium Truck",
    size: "4.5–6 Tonne",
    price: 89,
    img: "/images/truck-medium.png",
    bestFor: ["2 Bedroom Home", "Townhouse", "Medium Office", "Storage Moves"],
    includes: ["1 Professional Mover", "Tail Lift", "Moving Equipment", "Furniture Wrapping", "Assembly & Disassembly", "Safe Transportation"],
    featured: true,
  },
  {
    id: "large",
    name: "Large Truck",
    size: "8–10 Tonne",
    price: 109,
    img: "/images/truck-large.png",
    bestFor: ["3–5 Bedroom House", "Large Office Relocation", "Heavy Furniture", "Long Distance Moves"],
    includes: ["1 Professional Mover", "Large Capacity Truck", "Tail Lift", "Premium Equipment", "Furniture Protection", "Fast & Efficient Moving"],
    featured: false,
  },
];

const TruckCard = ({ truck, index, onBook }) => (
  <motion.div
    initial={{ opacity: 0, y: 56 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative flex flex-col border ${
      truck.featured ? "border-blaze bg-blaze/[0.04]" : "border-line bg-slate-100/70"
    }`}
    data-testid={`truck-card-${truck.id}`}
  >
    {truck.featured && (
      <span className="absolute -top-px right-0 bg-blaze px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white" data-testid="truck-featured-badge">
        Most Popular
      </span>
    )}

    <div className="relative overflow-hidden border-b border-line">
      <img
        src={truck.img}
        alt={`MoversNearby ${truck.name} (${truck.size}) with orange branding`}
        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
      <div className="absolute bottom-4 left-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blaze">{truck.size}</p>
        <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">{truck.name}</h3>
      </div>
    </div>

    <div className="flex items-end gap-2 px-6 pt-6">
      <span className="text-xs uppercase tracking-widest text-slate-500">From</span>
      <span className="font-display text-5xl font-semibold tracking-tighter text-slate-900" data-testid={`truck-price-${truck.id}`}>
        ${truck.price}
      </span>
      <span className="pb-1.5 text-sm text-slate-600">/hr</span>
    </div>
    <p className="px-6 pt-2 text-xs text-slate-500" data-testid={`truck-price-note-${truck.id}`}>
      Price includes 1 professional mover — every truck comes with a tail lift.
    </p>

    <div className="px-6 pt-5">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Best For</p>
      <div className="flex flex-wrap gap-2">
        {truck.bestFor.map((b) => (
          <span key={b} className="border border-line px-3 py-1.5 text-xs text-slate-700">{b}</span>
        ))}
      </div>
    </div>

    <div className="flex-1 px-6 pt-6">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Includes</p>
      <ul className="space-y-2.5">
        {truck.includes.map((inc) => (
          <li key={inc} className="flex items-start gap-3 text-sm text-slate-700">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-blaze" strokeWidth={2.5} />
            {inc}
          </li>
        ))}
      </ul>
    </div>

    <div className="p-6">
      <button
        onClick={() => onBook(truck)}
        data-testid={`book-${truck.id}-truck-btn`}
        className={`group/btn flex w-full items-center justify-center gap-3 px-6 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] ${
          truck.featured
            ? "bg-blaze text-white hover:shadow-[0_0_36px_rgba(37,99,235,0.45)]"
            : "border border-slate-300 text-slate-900 hover:border-blaze hover:text-blaze"
        }`}
      >
        Book {truck.name}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1.5" />
      </button>
    </div>
  </motion.div>
);

export default function Trucks() {
  const navigate = useNavigate();
  const book = (truck) => navigate({ pathname: "/", search: `?truck=${truck.id}`, hash: "#quote" });

  return (
    <main data-testid="trucks-page">
      <Seo {...PAGE_SEO.trucks} />
      <Header />

      <section className="relative overflow-hidden border-b border-line bg-white/70">
        <div className="pointer-events-none absolute -top-24 right-0 select-none font-display text-[22vw] font-semibold uppercase leading-none tracking-tighter text-stroke opacity-40 lg:text-[16rem]" aria-hidden="true">
          Trucks
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-40 lg:px-10 lg:pb-28 lg:pt-52">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Truck Pricing</p>
            <h1 className="max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="trucks-heading">
              Choose the right truck<br />for your <span className="text-blaze">move</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="trucks-subtitle">
              Whether you&rsquo;re moving a few items, an apartment, or a large family
              home, we have the perfect truck and experienced movers for every job.
              Transparent pricing, no hidden surprises, and professional service every time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="grid gap-6 lg:grid-cols-3">
          {TRUCKS.map((t, i) => <TruckCard key={t.id} truck={t} index={i} onBook={book} />)}
        </div>

        <Reveal className="mt-16 flex flex-wrap items-center justify-between gap-6 border border-line bg-slate-100/70 p-8 lg:p-10">
          <div>
            <h2 className="font-display text-2xl font-semibold uppercase tracking-tight lg:text-3xl">
              Not sure which truck fits?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Call us — we&rsquo;ll recommend the right truck in under a minute.
            </p>
          </div>
          <a
            href="tel:0391136005"
            data-testid="trucks-call-btn"
            className="flex items-center gap-3 bg-blaze px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_36px_rgba(37,99,235,0.45)]"
          >
            <Phone className="h-4 w-4" />
            Call 03 9113 6005
          </a>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
